import * as THREE from 'three';
import { isNewStyle } from '@/ui/artStyle';

/**
 * v1.4 新版色票：場景裡所有顏色都從這份色票拿。
 * - 牆：深灰藍；地板：依清潔等級各一色；設備：不鏽鋼淺灰；木頭；重點色只有 3 種（琥珀、青綠、珊瑚）。
 * - 內容 JSON 裡原本寫的顏色不用改：`tone()` 會把它「吸」到色票裡最接近的那一色（保留一點原色，東西還認得出來）。
 * - 經典版不會呼叫這裡（builders.litMat 只在新版才用 tone）。
 */
export const PALETTE = {
  // 牆與結構
  wall: '#3e4f5c', wallLight: '#c9d3d8', trim: '#6f8290',
  // 不鏽鋼與機台
  steel: '#cfd6da', steelMid: '#a9b4ba', steelDark: '#6f7c84', ink: '#2b3238',
  // 地板（清潔等級）
  floorPlain: '#e9e3d6', floorGray: '#aab3b7', floorGreen: '#86b795', floorTile: '#93a99b', floorCyan: '#79bcc8', floorOutdoor: '#6f7d69',
  // 木頭、紙箱
  wood: '#b48c63', woodDark: '#6e5640', kraft: '#d2ad7c',
  // 布料／制服
  white: '#ebe8e1', cream: '#f3eee4', skyblue: '#9fd3f5', navy: '#2f5f8f', grass: '#8fb573', gray: '#9aa3a8',
  // 重點色（只有 3 種）
  amber: '#f0a43a', teal: '#2f9e8f', coral: '#e0645a',
  // 食物與其他常見色（讓食材、貨品不會被吸成灰色）
  food: '#e9c6a5', sauce: '#a0622d', greens: '#7fbf6a', egg: '#f0e0a0', blue: '#4f86b8', pink: '#f4a7c0', black: '#1d2226',
} as const;

const SWATCHES = Object.values(PALETTE).map(c => new THREE.Color(c));
const cache = new Map<string, THREE.Color>();
const tmp = new THREE.Color();

/** 把任意顏色吸到色票上：找最接近的一色，和原色 65%／35% 混合 */
export function tone(c: THREE.ColorRepresentation): THREE.Color {
  const key = String(c);
  const hit = cache.get(key); if (hit) return hit.clone();
  tmp.set(c);
  let best = SWATCHES[0], bd = Infinity;
  for (const s of SWATCHES) {
    const dr = s.r - tmp.r, dg = s.g - tmp.g, db = s.b - tmp.b;
    const d = dr * dr * 0.3 + dg * dg * 0.59 + db * db * 0.11;
    if (d < bd) { bd = d; best = s; }
  }
  const out = tmp.clone().lerp(best, 0.65);
  cache.set(key, out);
  return out.clone();
}

/** 新版地板色（依清潔等級）；經典版回傳 null（用原本的顏色） */
export function floorColorNew(kind: string): string | null {
  if (!isNewStyle()) return null;
  return ({ 'epoxy-cyan': PALETTE.floorCyan, 'epoxy-green': PALETTE.floorGreen, 'tile-graygreen': PALETTE.floorTile, 'epoxy-gray': PALETTE.floorGray, 'plain-white': PALETTE.floorPlain, outdoor: PALETTE.floorOutdoor } as Record<string, string>)[kind] ?? null;
}
